import { Fragment, useEffect, useRef, useState } from 'react';
import { FiClock, FiDroplet, FiLoader, FiThermometer, FiWind, FiX } from 'react-icons/fi';
import { weatherService } from '../../services/weatherService';
import { useShallow } from 'zustand/react/shallow';
import { type WeatherData, useWeatherStore } from '../../store/weatherStore';
import { WeatherIcon } from './WeatherIcon';
import { useTranslation } from '../../i18n/LanguageContext';
import { rasterSampler } from '../../services/rasterSampler';
import { PRECIP_ACCUMULATION_HOURS } from './hoverValueFormat';
import { thunderstormCode } from './thunderCriteria';

/**
 * كم ساعة قادمة نفحصها بحثاً عن عواصف رعدية. اللوحة تعرض كل ساعات التوقّع (قد تتجاوز
 * 120)، وفحص كلٍّ منها يعني تحميل ثلاثة نُسج لكل ساعة — ثمن باهظ بلا مقابل: المستخدم
 * لا يرى إلا الساعات القريبة قبل أن يمرّر. نفحص نافذة من الساعة المختارة فما بعدها،
 * وما وراءها يبقى على كود الحالة القادم من الخادم كما كان.
 */
const THUNDER_SCAN_HOURS = 24;

/** نصف قطر البحث عن ذروة K-index حول النقطة (بالدرجات) — ≈ 25 كم، مقياس خليّة رعدية. */
const THUNDER_RADIUS_DEG = 0.25;

interface WeatherInfoPanelProps {
    weatherData: WeatherData | null;
    currentTimeIndex: number;
    /** زمن الـ timeline الفعلي (إيبوك بالثواني) — للمطابقة بالزمن لا بالفهرس. */
    targetEpoch?: number;
    /** عدد إطارات الـ timeline — لقصّ النقر ضمن مدى الإطارات المتاحة. */
    frameCount?: number;
    location: { lat: number; lon: number } | null;
    onRetry?: () => void;
}

function clamp(value: number, min: number, max: number) {
    return Math.min(max, Math.max(min, value));
}

function formatCoordinate(value: number, positive: string, negative: string) {
    const absolute = Math.abs(value);
    let degrees = Math.floor(absolute);
    let minutes = Math.round((absolute - degrees) * 60);

    if (minutes === 60) {
        degrees += 1;
        minutes = 0;
    }

    return `${degrees}° ${String(minutes).padStart(2, '0')}' ${value >= 0 ? positive : negative}`;
}

function formatHour(date: Date) {
    return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
}

function formatUtcOffset(date: Date) {
    const offsetHours = -date.getTimezoneOffset() / 60;
    const sign = offsetHours >= 0 ? '+' : '-';
    return `UTC${sign}${Math.abs(offsetHours)}`;
}

/** فاصل اليوم فوق أوّل ساعة من كل يوم — بلغة الواجهة (مفاتيح day.0..6 وmonth.1..12). */
function formatDayDivider(date: Date, previousDate: Date | null, t: (key: string) => string) {
    if (!previousDate || date.toDateString() !== previousDate.toDateString()) {
        return `${t(`day.${date.getDay()}`)} ${date.getDate()} ${t(`month.${date.getMonth() + 1}`)}`;
    }

    return null;
}

export function WeatherInfoPanel({ weatherData, currentTimeIndex, targetEpoch, frameCount, location, onRetry }: WeatherInfoPanelProps) {
    const { t } = useTranslation();
    const activeRowRef = useRef<HTMLButtonElement | null>(null);
    // النُسج تصل لا تزامنياً، والقراءة منها أثناء العرض تُرجع null قبل وصولها. هذا العدّاد
    // يُعيد العرض مرّة بعد اكتمال التحميل كي تظهر أيقونة العاصفة بدل بقائها على كود الخادم.
    const [thunderReady, setThunderReady] = useState(0);

    // تمرير الصف النشط للعرض عند تغيّر زمن الـ timeline (يبقى التظليل مرئياً دائماً).
    // inline لازم لشريط الساعات الأفقي على الجوّال (block وحده لا يمرّر أفقياً)، وهو بلا
    // أثر على القائمة الرأسية في الديسكتوب.
    useEffect(() => {
        activeRowRef.current?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }, [targetEpoch, currentTimeIndex, weatherData]);

    // إحماء نُسج معايير البرق (K-index + الهطول + الحرارة) للساعة المختارة ونافذة ما بعدها،
    // ثم إعادة عرض واحدة. الهطول تراكم 3 ساعات فيحتاج الساعات السابقة أيضاً.
    useEffect(() => {
        if (!location) return;
        let cancelled = false;
        const start = Math.max(0, currentTimeIndex - PRECIP_ACCUMULATION_HOURS + 1);
        for (let i = start; i < currentTimeIndex + THUNDER_SCAN_HOURS; i += 1) {
            rasterSampler.prefetch('k-index', i);
            rasterSampler.prefetch('precipitation', i);
            rasterSampler.prefetch('temperature', i);
        }
        Promise.allSettled([
            rasterSampler.ensure('k-index', currentTimeIndex),
            rasterSampler.ensure('precipitation', currentTimeIndex),
            rasterSampler.ensure('temperature', currentTimeIndex),
        ]).then(() => { if (!cancelled) setThunderReady((n) => n + 1); });
        return () => { cancelled = true; };
    }, [location, currentTimeIndex]);
    void thunderReady; // القيمة نفسها غير مستعملة؛ الغرض إعادة العرض بعد وصول النُسج.
    const {
        units,
        selectedModel,
        setCurrentTimeIndex,
        setInfoPanelOpen,
        setSelectedModel,
        availableModels,
        isLoading,
    } = useWeatherStore(useShallow((s) => ({ units: s.units, selectedModel: s.selectedModel, setCurrentTimeIndex: s.setCurrentTimeIndex, setInfoPanelOpen: s.setInfoPanelOpen, setSelectedModel: s.setSelectedModel, availableModels: s.availableModels, isLoading: s.isLoading })));

    const coordsLabel = location
        ? `${formatCoordinate(location.lat, t('coord.N'), t('coord.S'))} , ${formatCoordinate(location.lon, t('coord.E'), t('coord.W'))}`
        : t('weather.selectedLocation');

    // حالة عدم وجود بيانات بعد: نُظهر اللوحة دائماً (تحميل أو خطأ) بدل ألا تظهر إطلاقاً —
    // فالضغط على الإحداثية يجب أن يفتح اللوحة حتى لو تأخّر/فشل طلب الـ API.
    const times = weatherData?.hourly.time ?? [];
    if (!weatherData || !times.length) {
        return (
            <div className="absolute top-[82px] left-[72px] w-[248px] max-h-[min(56vh,470px)] z-[750] overflow-hidden flex flex-col rounded-[14px] bg-[rgba(28,33,45,0.94)] light:bg-[var(--panel-bg)] backdrop-blur-[22px] border border-white/[0.08] light:border-[var(--panel-border)] light:text-[var(--text-primary)] shadow-[0_20px_54px_rgba(0,0,0,0.42)] light:shadow-[var(--panel-shadow)] max-[768px]:top-auto max-[768px]:inset-x-2 max-[768px]:bottom-[calc(78px+env(safe-area-inset-bottom))] max-[768px]:transform-none max-[768px]:w-auto max-[768px]:max-w-none max-[768px]:rounded-2xl max-[768px]:z-[2050] max-[768px]:max-h-[min(52vh,340px)] max-[768px]:shadow-[0_18px_44px_rgba(0,0,0,0.5)] max-[768px]:before:content-[''] max-[768px]:before:absolute max-[768px]:before:top-[6px] max-[768px]:before:left-1/2 max-[768px]:before:-translate-x-1/2 max-[768px]:before:w-9 max-[768px]:before:h-1 max-[768px]:before:rounded-full max-[768px]:before:bg-white/[0.22] max-[768px]:before:pointer-events-none max-[768px]:before:z-[2]">
                <div className="px-3 pt-2 pb-[7px] border-b border-white/[0.06] light:border-[var(--divider)] flex items-start justify-between relative gap-2.5 max-[768px]:pt-[14px]">
                    <button
                        className="w-7 h-7 flex items-center justify-center bg-white/[0.06] light:bg-black/5 border-none rounded-lg text-inherit light:text-[var(--text-secondary)] cursor-pointer text-sm transition-all duration-[0.18s] ease shrink-0 hover:bg-[rgba(255,80,80,0.16)] hover:text-[#ff8d8d] light:hover:bg-[rgba(220,38,38,0.10)] light:hover:text-[#dc2626]"
                        onClick={() => setInfoPanelOpen(false)}
                        title={t('common.close')}
                        type="button"
                    >
                        <FiX />
                    </button>
                    <div className="flex-1 min-w-0">
                        <div className="text-[13px] font-extrabold tracking-[0.01em] [direction:ltr]">{coordsLabel}</div>
                        <div className="flex justify-between gap-1.5 mt-1 text-[11px] text-[rgba(230,237,243,0.68)] light:text-[var(--text-secondary)]"><span>{t('weather.hourly')}</span></div>
                    </div>
                </div>

                <div className="flex flex-col items-center justify-center gap-3 px-5 py-8 text-center text-[13px] text-[rgba(230,237,243,0.7)] [&_.panel-spinner]:w-[26px] [&_.panel-spinner]:h-[26px]">
                    {isLoading ? (
                        <>
                            <FiLoader className="panel-spinner text-[32px] animate-spin-360 text-[#95f3a4]" />
                            <span>{t('weather.fetching')}</span>
                        </>
                    ) : (
                        <>
                            <span>{t('weather.fetchFailed')}</span>
                            {onRetry && (
                                <button className="px-[18px] py-2 border border-[var(--border-color)] rounded-full bg-[#6d5efc] text-white text-[13px] font-semibold cursor-pointer hover:bg-[#5b4ce0]" onClick={onRetry} type="button">
                                    {t('common.retry')}
                                </button>
                            )}
                        </>
                    )}
                </div>
            </div>
        );
    }

    // المطابقة بالزمن الفعلي للـ timeline لا بالفهرس: محورا الزمن مختلفان (raster مربوط بدورة
    // GFS، وOpen-Meteo يبدأ من منتصف الليل)، فالفهرسة المباشرة كانت تُظهر ساعة/حرارة خاطئة.
    const safeIndex = (() => {
        if (targetEpoch === undefined) return clamp(currentTimeIndex, 0, times.length - 1);
        const targetMs = targetEpoch * 1000;
        let best = 0;
        let bestDiff = Infinity;
        for (let i = 0; i < times.length; i += 1) {
            const diff = Math.abs(Number(times[i]) * 1000 - targetMs);
            if (diff < bestDiff) { bestDiff = diff; best = i; }
        }
        return best;
    })();
    const currentDate = new Date(Number(times[safeIndex]) * 1000);

    /**
     * ترقية كود الحالة إلى عاصفة رعدية (95/96) عند تحقّق معايير البرق نفسها التي ترسم
     * بها الخريطة صواعقها — راجع thunderCriteria.
     *
     * ضروري لأن كود الحالة القادم من /point مشتقّ من الغيوم والهطول فقط
     * (PointController::weatherCode)، فلا يُخرج 95/96 مهما اشتدّت العاصفة: كانت الخريطة
     * تشتعل صواعق فوق النقطة بينما تقول اللوحة «مطر متوسط» بأيقونة مطر. لا يُغيّر هذا
     * موضع أيّ صاعقة على الخريطة ولا عتباتها؛ يقرأ نفس النُسج بنفس الشروط ليتّفق العرضان.
     */
    const thunderAt = (idx: number): number | null => {
        if (!location) return null;
        // ذروة K-index في مربّع صغير حول النقطة لا القيمة عندها بالضبط — نفس ما تفعله
        // الطبقة: هي تضع الصاعقة على ذروة خليّتها، فلو قِسنا النقطة وحدها لظهرت صواعق
        // على الشاشة حول موضع النقر بينما تقول اللوحة «لا عاصفة». ±0.25° ≈ 25 كم، وهو
        // مقياس خليّة رعدية نمطية.
        const peak = rasterSampler.maxScalarInBoxAt(
            'k-index', idx, 0,
            location.lat - THUNDER_RADIUS_DEG, location.lat + THUNDER_RADIUS_DEG,
            location.lon - THUNDER_RADIUS_DEG, location.lon + THUNDER_RADIUS_DEG,
        );
        return thunderstormCode(
            peak ? peak.value : null,
            rasterSampler.sampleAccumulated('precipitation', idx, location.lat, location.lon, PRECIP_ACCUMULATION_HOURS),
            rasterSampler.sampleScalar('temperature', idx, location.lat, location.lon),
        );
    };

    const currentWeatherCode = thunderAt(safeIndex) ?? weatherData.hourly.weather_code?.[safeIndex] ?? 0;
    // decodeWeatherCodeAdjusted لا decodeWeatherCode: رمز النموذج يصف الهطول «مطراً»
    // أحياناً حتى تحت الصفر، فكانت اللوحة تعرض «مطر خفيف» وأيقونة مطر عند ‑14°.
    const currentWeather = weatherService.decodeWeatherCodeAdjusted(
        currentWeatherCode,
        weatherService.isDaytime(currentDate),
        weatherData.hourly.temperature_2m?.[safeIndex],
        weatherData.hourly.snowfall?.[safeIndex],
    );
    // وصف الحالة يأتي إنجليزياً من الخدمة؛ نترجمه بمفتاح wx.<condition>، وإن لم يوجد
    // المفتاح يعيد t المفتاح نفسه فنسقط على وصف الخدمة الأصلي بدل عرض نصّ المفتاح.
    const conditionKey = `wx.${currentWeather.condition}`;
    const translatedCondition = t(conditionKey);
    const currentCondition = translatedCondition === conditionKey ? currentWeather.description : translatedCondition;

    const formatTemperature = (value?: number) => {
        if (value === undefined || Number.isNaN(value)) {
            return '--';
        }

        const converted = units.temperature === 'fahrenheit'
            ? weatherService.convertTemperature(value, true)
            : value;

        return `${Math.round(converted)}°`;
    };

    const formatPrecipitation = (value?: number) => {
        if (value === undefined || Number.isNaN(value)) {
            return '0';
        }

        const converted = units.precipitation === 'inch'
            ? weatherService.convertPrecipitation(value, true)
            : value;

        return converted < 10 ? converted.toFixed(1) : Math.round(converted).toString();
    };

    const formatWind = (value?: number) => {
        if (value === undefined || Number.isNaN(value)) {
            return '--';
        }

        const converted = weatherService.convertWindSpeed(value, units.wind);
        const suffix = units.wind === 'mph'
            ? 'mph'
            : units.wind === 'knots'
                ? 'kt'
                : units.wind === 'ms'
                    ? 'm/s'
                    : 'km/h';

        return `${Math.round(converted)} ${suffix}`;
    };

    const temperature = weatherData.hourly.temperature_2m?.[safeIndex];
    const apparentTemperature = weatherData.hourly.apparent_temperature?.[safeIndex];
    const precipitation = weatherData.hourly.precipitation?.[safeIndex];
    const humidity = weatherData.hourly.relative_humidity_2m?.[safeIndex];
    const windSpeed = weatherData.hourly.wind_speed_10m?.[safeIndex];

    // نعرض الساعة المختارة وجميع الساعات اللاحقة فقط. عند تحريك الـ timeline
    // أو وصوله لساعة جديدة تختفي الساعات الماضية من القائمة، مثل Zoom Earth.
    const rowStart = safeIndex;
    // لا نقتطع الساعات القادمة عند نهاية نافذة 48 ساعة؛ يجب أن تبقى كل
    // الساعات والأيام اللاحقة ظاهرة حتى نهاية البيانات المتاحة.
    const rowEnd = times.length;
    const rows = [];
    let previousDate: Date | null = null;

    for (let index = rowStart; index < rowEnd; index += 1) {
        const rowDate = new Date(Number(times[index]) * 1000);
        const rowWeather = weatherService.decodeWeatherCodeAdjusted(
            (index - rowStart < THUNDER_SCAN_HOURS ? thunderAt(index) : null)
                ?? weatherData.hourly.weather_code?.[index] ?? 0,
            weatherService.isDaytime(rowDate),
            weatherData.hourly.temperature_2m?.[index],
            weatherData.hourly.snowfall?.[index],
        );

        rows.push({
            index,
            date: rowDate,
            divider: formatDayDivider(rowDate, previousDate, t),
            temperature: weatherData.hourly.temperature_2m?.[index],
            precipitation: weatherData.hourly.precipitation?.[index],
            weatherIcon: rowWeather.icon,
        });

        previousDate = rowDate;
    }

    return (
        <div className={"absolute top-[82px] left-[72px] w-[248px] max-h-[min(56vh,470px)] z-[750] overflow-hidden flex flex-col rounded-[14px] bg-[rgba(28,33,45,0.94)] light:bg-[var(--panel-bg)] backdrop-blur-[22px] border border-white/[0.08] light:border-[var(--panel-border)] light:text-[var(--text-primary)] shadow-[0_20px_54px_rgba(0,0,0,0.42)] light:shadow-[var(--panel-shadow)] max-[768px]:top-auto max-[768px]:inset-x-2 max-[768px]:bottom-[calc(78px+env(safe-area-inset-bottom))] max-[768px]:transform-none max-[768px]:w-auto max-[768px]:max-w-none max-[768px]:rounded-2xl max-[768px]:z-[2050] max-[768px]:max-h-[min(52vh,340px)] max-[768px]:shadow-[0_18px_44px_rgba(0,0,0,0.5)] max-[768px]:before:content-[''] max-[768px]:before:absolute max-[768px]:before:top-[6px] max-[768px]:before:left-1/2 max-[768px]:before:-translate-x-1/2 max-[768px]:before:w-9 max-[768px]:before:h-1 max-[768px]:before:rounded-full max-[768px]:before:bg-white/[0.22] max-[768px]:before:pointer-events-none max-[768px]:before:z-[2]"}>
            {isLoading && (
                <div className="absolute inset-0 bg-[rgba(28,33,45,0.4)] backdrop-blur-[2px] flex flex-col items-center justify-center gap-3 z-[100] text-white text-sm font-bold animate-fade-in [animation-duration:0.3s]">
                    <FiLoader className="panel-spinner text-[32px] animate-spin-360 text-[#95f3a4]" />
                    <span>{t('weather.updating')}</span>
                </div>
            )}
            <div className="px-3 pt-2 pb-[7px] border-b border-white/[0.06] light:border-[var(--divider)] flex items-start justify-between relative gap-2.5 max-[768px]:pt-[14px]">
                <button
                    className="w-7 h-7 flex items-center justify-center bg-white/[0.06] light:bg-black/5 border-none rounded-lg text-inherit light:text-[var(--text-secondary)] cursor-pointer text-sm transition-all duration-[0.18s] ease shrink-0 hover:bg-[rgba(255,80,80,0.16)] hover:text-[#ff8d8d] light:hover:bg-[rgba(220,38,38,0.10)] light:hover:text-[#dc2626]"
                    onClick={() => setInfoPanelOpen(false)}
                    title={t('common.close')}
                    type="button"
                >
                    <FiX />
                </button>

                <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-extrabold tracking-[0.01em] [direction:ltr]">
                        {coordsLabel}
                    </div>
                    <div className="flex justify-between gap-1.5 mt-1 text-[11px] text-[rgba(230,237,243,0.68)] light:text-[var(--text-secondary)]">
                        <span>{t('weather.hourly')}</span>
                        <span>{weatherData.timezone_abbreviation || formatUtcOffset(currentDate)}</span>
                    </div>
                </div>
            </div>

            <div className="flex items-center justify-between gap-2.5 px-3 py-[7px] bg-[linear-gradient(180deg,rgba(255,255,255,0.03),rgba(255,255,255,0))] light:bg-black/[0.02] border-b border-white/[0.06] light:border-[var(--divider)]">
                <div className="flex items-center gap-3 min-w-0">
                    <span className="min-w-[38px] px-[7px] py-1 rounded-full bg-[linear-gradient(135deg,#95f3a4_0%,#dfff7e_100%)] text-[#1a270d] text-base font-black leading-none text-center">{formatTemperature(temperature)}</span>
                    <div className="min-w-0">
                        <div className="text-[13px] font-bold">{currentCondition}</div>
                        <div className="mt-0.5 text-[11px] text-[rgba(230,237,243,0.64)] light:text-[var(--text-secondary)]">{t('weather.feelsLike', { v: formatTemperature(apparentTemperature) })}</div>
                    </div>
                </div>

                <div className="flex flex-col items-end gap-1.5 shrink-0">
                    <div className="text-[22px] leading-none flex items-center justify-center w-[38px] h-[38px] rounded-[10px] bg-[rgba(11,15,20,0.55)]">
                        <WeatherIcon icon={currentWeather.icon} size={34} title={currentCondition} />
                    </div>
                    <div className="flex items-center gap-1 text-[11px] font-bold text-[rgba(230,237,243,0.74)] light:text-[var(--text-secondary)]">
                        <FiClock />
                        <span>{formatHour(currentDate)}</span>
                    </div>
                </div>
            </div>

            {weatherData.stale && (
                <div className="mt-2 mx-3 mb-0 px-[9px] py-[7px] rounded-[9px] bg-[rgba(255,176,32,0.12)] light:bg-[rgba(217,119,6,0.10)] border border-[rgba(255,176,32,0.2)] light:border-[rgba(217,119,6,0.22)] text-[#ffd27a] light:text-[#92400e] text-[11px] leading-[1.45]">
                    {weatherData.providerMessage || t('weather.cached')}
                </div>
            )}

            <div className="grid grid-cols-3 gap-[5px] px-2.5 py-[5px] border-b border-white/[0.05] light:border-[var(--divider)]">
                <div className="flex items-center justify-center gap-[5px] px-[3px] py-[5px] bg-white/[0.04] light:bg-[var(--stat-bg)] rounded-[10px] text-[11px] font-bold text-[rgba(230,237,243,0.78)] light:text-[var(--text-primary)]">
                    <FiWind />
                    <span>{formatWind(windSpeed)}</span>
                </div>
                <div className="flex items-center justify-center gap-[5px] px-[3px] py-[5px] bg-white/[0.04] light:bg-[var(--stat-bg)] rounded-[10px] text-[11px] font-bold text-[rgba(230,237,243,0.78)] light:text-[var(--text-primary)]">
                    <FiDroplet />
                    <span>{formatPrecipitation(precipitation)} {units.precipitation === 'inch' ? 'in' : 'mm'}</span>
                </div>
                <div className="flex items-center justify-center gap-[5px] px-[3px] py-[5px] bg-white/[0.04] light:bg-[var(--stat-bg)] rounded-[10px] text-[11px] font-bold text-[rgba(230,237,243,0.78)] light:text-[var(--text-primary)]">
                    <FiThermometer />
                    <span>{humidity !== undefined ? `${Math.round(humidity)}%` : '--'}</span>
                </div>
            </div>

            <div className="grid grid-cols-[42px_40px_38px_1fr] gap-1 items-center px-3 pt-[5px] pb-[3px] text-[10px] font-bold text-[rgba(230,237,243,0.46)] light:text-[var(--text-muted)] [direction:ltr] [&>span:last-child]:text-end max-[768px]:hidden">
                <span>{t('weather.colTemp')}</span>
                <span>{t('weather.colPrecip')}</span>
                <span>{t('weather.colState')}</span>
                <span>{t('weather.colHour')}</span>
            </div>

            <div className="flex-1 overflow-y-auto pt-0 px-0 pb-1 [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-thumb]:bg-white/[0.12] light:[&::-webkit-scrollbar-thumb]:bg-black/[0.12] [&::-webkit-scrollbar-thumb]:rounded-full max-[768px]:flex max-[768px]:flex-[0_1_auto] max-[768px]:min-h-0 max-[768px]:gap-1.5 max-[768px]:p-2 max-[768px]:overflow-x-auto max-[768px]:overflow-y-hidden max-[768px]:[scroll-snap-type:x_proximity] max-[768px]:[-webkit-overflow-scrolling:touch] max-[768px]:[scrollbar-width:none] max-[768px]:[&::-webkit-scrollbar]:hidden">
                {rows.map((row) => (
                    <Fragment key={times[row.index]}>
                        {row.divider && <div className="px-[14px] pt-1.5 pb-[3px] text-[11px] font-bold text-[rgba(230,237,243,0.44)] light:text-[var(--text-muted)] max-[768px]:flex-[0_0_auto] max-[768px]:w-11 max-[768px]:px-0.5 max-[768px]:py-2 max-[768px]:flex max-[768px]:items-center max-[768px]:text-center max-[768px]:whitespace-normal max-[768px]:leading-[1.35] max-[768px]:text-[9px] max-[768px]:border-s max-[768px]:border-white/[0.12]">{row.divider}</div>}

                        <button
                            ref={row.index === safeIndex ? activeRowRef : undefined}
                            className={`w-[calc(100%-8px)] mx-1 grid grid-cols-[42px_40px_38px_1fr] gap-1 items-center px-2 py-1 border-none rounded-xl text-inherit cursor-pointer [direction:ltr] transition-[background,transform] duration-[0.16s] ease max-[768px]:flex-[0_0_auto] max-[768px]:w-[52px] max-[768px]:m-0 max-[768px]:flex max-[768px]:flex-col max-[768px]:items-center max-[768px]:gap-1 max-[768px]:px-[3px] max-[768px]:py-1.5 max-[768px]:[scroll-snap-align:start] ${row.index === safeIndex ? 'bg-white/[0.12] light:bg-[var(--row-active)] shadow-[inset_0_0_0_1px_rgba(255,255,255,0.05)] light:shadow-[inset_0_0_0_1px_rgba(0,0,0,0.06)] max-[768px]:bg-white/10 max-[768px]:light:bg-black/[0.08] max-[768px]:shadow-[inset_0_0_0_1.5px_rgba(149,243,164,0.75)] max-[768px]:light:shadow-[inset_0_0_0_1.5px_rgba(58,166,85,0.8)]' : 'bg-transparent hover:bg-white/[0.06] light:hover:bg-[var(--row-hover)] max-[768px]:bg-white/[0.04] max-[768px]:light:bg-black/[0.05]'}`}
                            onClick={() => {
                                // المحوران مختلفان: نحرّك الـ timeline بإزاحة الساعات نفسها بين
                                // الصف المنقور والصف النشط، مقصوصةً ضمن إطارات الـ timeline.
                                const delta = row.index - safeIndex;
                                const maxFrame = frameCount && frameCount > 0 ? frameCount - 1 : currentTimeIndex + delta;
                                setCurrentTimeIndex(clamp(currentTimeIndex + delta, 0, maxFrame));
                            }}
                            type="button"
                        >
                            <span className="justify-self-start min-w-[36px] px-1.5 py-[3px] rounded-full bg-[linear-gradient(135deg,#95f3a4_0%,#dfff7e_100%)] text-[#1a270d] text-xs font-black text-center max-[768px]:order-3 max-[768px]:justify-self-auto max-[768px]:min-w-0 max-[768px]:px-1.5 max-[768px]:py-0.5">{formatTemperature(row.temperature)}</span>
                            <span className="text-xs font-bold text-[rgba(230,237,243,0.78)] light:text-[var(--text-secondary)] text-center max-[768px]:order-4 max-[768px]:text-[11px] max-[768px]:opacity-75">
                                {formatPrecipitation(row.precipitation)}
                            </span>
                            <span className="text-lg text-center flex items-center justify-center max-[768px]:order-2 [&_.weather-icon]:w-[22px] [&_.weather-icon]:h-[22px] [&_.weather-icon]:p-[3px] [&_.weather-icon]:rounded-lg [&_.weather-icon]:bg-[rgba(11,15,20,0.45)] [&_.weather-icon]:[box-sizing:content-box] max-[768px]:[&_.weather-icon]:w-5 max-[768px]:[&_.weather-icon]:h-5 max-[768px]:[&_.weather-icon]:p-0.5">
                                <WeatherIcon icon={row.weatherIcon} size={20} />
                            </span>
                            <span className="text-xs font-extrabold text-[rgba(230,237,243,0.88)] light:text-[var(--text-primary)] text-end max-[768px]:order-1 max-[768px]:text-[11px]">{formatHour(row.date)}</span>
                        </button>
                    </Fragment>
                ))}
            </div>
        </div>
    );
}
